import type { BackendGiftListProductItem } from "./backend-gift-list-schema";

/**
 * What a list item's product says about its taxonomy, in the words of `ListedPiece`
 * (`category`, `type`, `material`).
 *
 * Pure, like every mapper: the list's category aggregate arrives as an argument. Anything the
 * response does not carry reads **empty by name**, never invented (Gate_Externo).
 */

/** A category of the list's `filters.categories`: the only part the join needs. */
export type ListCategory = { readonly id: string; readonly name: string };

/**
 * The item's attributes are named by their display name ("Tipo de Produto"), where the catalog's
 * aggregate speaks slugs (`CATALOG_TYPE_ATTRIBUTE`). So the name is compared normalised: case and
 * accents do not tell two spellings of the same attribute apart.
 */
function normalizeName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const TYPE_ATTRIBUTE_NAME = normalizeName("Tipo de Produto");

/**
 * "Material Principal" only. "Material de Apoio" exists too and is deliberately left out — the
 * rail has one Material facet, not two, and the supporting material is not the piece's material
 * (the same ruling `catalog-product.ts` holds for the catalog).
 */
const MATERIAL_ATTRIBUTE_NAME = normalizeName("Material Principal");

type ItemAttributes = NonNullable<BackendGiftListProductItem["product"]["attributes"]>;

/** The value of the first attribute carrying this name, or "" when the item has none. */
function attributeValue(attributes: ItemAttributes | undefined, normalizedName: string): string {
  const found = (attributes ?? []).find((attribute) => normalizeName(attribute.name) === normalizedName);

  return found?.value.trim() ?? "";
}

/**
 * The categories a list's detail answers with, for the join above.
 *
 * Read from the **first page**: the aggregate is counted over the whole list and not the page
 * (measured 2026-10-20), so every later page would answer the same. A detail without `filters` —
 * or without `categories`, as an answer scoped by category omits them — has none to give.
 */
export function listCategoriesOf(
  filters: { readonly categories?: readonly ListCategory[] } | undefined,
): readonly ListCategory[] {
  return (filters?.categories ?? []).map(({ id, name }) => ({ id, name }));
}

export type ItemTaxonomy = { readonly category: string; readonly type: string; readonly material: string };

/**
 * The category's **name**, by the product's `categoryId`, from the list's own aggregate.
 *
 * Matched by id, which a rename in the ERP leaves alone. An id the aggregate does not list — or
 * no aggregate at all — is "", not a guess.
 */
export function categoryNameOf(
  categoryId: string | null | undefined,
  categories: readonly ListCategory[],
): string {
  if (categoryId === null || categoryId === undefined) return "";

  return categories.find((category) => category.id === categoryId)?.name ?? "";
}

export function itemTaxonomy(
  product: BackendGiftListProductItem["product"],
  categories: readonly ListCategory[],
): ItemTaxonomy {
  return {
    category: categoryNameOf(product.categoryId, categories),
    type: attributeValue(product.attributes, TYPE_ATTRIBUTE_NAME),
    material: attributeValue(product.attributes, MATERIAL_ATTRIBUTE_NAME),
  };
}
